import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useActionSlow, useToast } from '@zarya/ui';
import { isApiError, type ApiError } from '@app/api/ApiError';
import { errorSpec, hasErrorMessage } from '@app/errors';
import { hour } from '@app/format';
import { hasKey, t, tn } from '@app/i18n';
import { approvalsKeys } from '@app/main/Approvals/Approvals.api';
import { identitiesKeys } from '@app/main/Identities/Identities.api';
import { overviewKeys } from '@app/main/Overview/Overview.api';
import { tenantKeys } from '@app/main/TenantSettings/TenantSettings.api';
import {
  discoveryKeys,
  useDiscoveryStatus,
  useTriggerDiscovery,
  type DiscoveryResult,
} from './discovery.api';

/**
 * O ciclo inteiro do «Discovery agora» numa peça só (29/08): dispara, acompanha,
 * anuncia.
 *
 * Está aqui, e não em cada botão, porque **três telas** têm o mesmo botão (T3, T4
 * e o card de T21) e a moldura precisa do mesmo acompanhamento sem ter botão
 * nenhum — é o que põe no indicador a varredura que outra pessoa começou. Três
 * cópias desta máquina de estados é como uma delas para de invalidar a lista de
 * identidades e ninguém percebe por uma semana.
 *
 * O registro no indicador é **idempotente por chave** (`manter`/`soltar`), então
 * a moldura e o botão acompanhando ao mesmo tempo mostram **uma** tarefa.
 */

/** Como a varredura aparece no indicador de tarefas. */
const WAIT_TITLE = 'Discovery';

/** Chave da tarefa no indicador — a mesma em toda a árvore, de propósito. */
const WAIT_KEY = 'discovery';

/** `409` do contrato: já existe varredura. Informação, não falha. */
const CODE_IN_EXECUTION = 'discovery_em_execucao';

export interface DiscoveryOptions {
  /**
   * Erro do **disparo** (403, executor sem URL, rede). Chamado com `null` quando
   * um novo clique começa bem — o banner anterior sai da tela.
   *
   * `409 discovery_em_execucao` **não** passa por aqui: já há varredura correndo,
   * e isso é informação, não falha. Vira toast e o acompanhamento começa.
   */
  onErro?: (erro: ApiError | null) => void;
  /**
   * Aviso da varredura **terminada**: o `error_code` de quem falhou no meio, ou
   * o primeiro item de `warnings[]`. `null` quando a varredura fechou limpa.
   *
   * É por aqui que a falha do executor chega à tela desde que a varredura passou
   * a correr fora da requisição — ela não tem mais como voltar num 502.
   */
  onAviso?: (aviso: string | null) => void;
}

export interface DiscoveryNow {
  /** Há varredura correndo agora (a minha, a de outra pessoa, ou a do periódico). */
  emExecucao: boolean;
  /** Quando a varredura corrente começou (ISO), ou `null`. */
  iniciadoEm: string | null;
  /** Quem disparou a varredura corrente, ou `null`. */
  iniciadoPor: string | null;
  /** O `POST` está em voo — o botão fica ocupado por este instante, não pela varredura. */
  disparando: boolean;
  /** Pede um tique. Não espera a varredura: o acompanhamento faz o resto. */
  executar: () => Promise<void>;
}

export function useDiscoveryNow(opcoes: DiscoveryOptions = {}): DiscoveryNow {
  const { onErro, onAviso } = opcoes;

  const status = useDiscoveryStatus();
  const disparo = useTriggerDiscovery();
  const { manter, soltar } = useActionSlow();
  const toast = useToast();
  const queryClient = useQueryClient();

  const inExecution = status.data?.running ?? false;

  // O efeito de fecho não pode depender de `status.data`: o objeto é novo a cada
  // consulta (de 3 em 3 segundos), e o efeito rodaria sem nada ter mudado.
  const lastResultRef = useRef<DiscoveryResult | null>(null);
  lastResultRef.current = status.data?.last_result ?? null;

  // Quem acompanha **por ter clicado** anuncia o fecho; quem só chegou na tela com
  // a varredura em curso, não. Sem esta distinção, abrir o console durante o tique
  // periódico terminaria num toast sobre algo que o usuário não pediu.
  //
  // `cliqueEmRef` é o carimbo do clique, e existe por uma armadilha concreta: com
  // uma varredura curta, o `running: true` do `202` e o `false` da consulta
  // seguinte chegam perto o bastante para o React juntá-los num render só — a tela
  // **nunca** vê o estado "correndo", e um fecho que dependesse de tê-lo visto
  // ficaria mudo. Comparando o `dataUpdatedAt` com o carimbo, "parado, com dado
  // colhido depois do meu clique" já é o fecho.
  const meuRef = useRef(false);
  const cliqueEmRef = useRef(0);
  const warningRef = useRef(onAviso);
  warningRef.current = onAviso;

  // Enquanto houver varredura, há tarefa no indicador — de quem quer que ela seja.
  useEffect(() => {
    if (inExecution) manter(WAIT_KEY, { titulo: WAIT_TITLE });
    else soltar(WAIT_KEY);
  }, [inExecution, manter, soltar]);

  // Fecho: a varredura acabou.
  const rodavaRef = useRef(false);
  const colhidoEm = status.dataUpdatedAt;
  useEffect(() => {
    const rodava = rodavaRef.current;
    rodavaRef.current = inExecution;
    if (inExecution) return;

    const meu = meuRef.current && colhidoEm >= cliqueEmRef.current;
    // Nem vi correndo, nem cliquei: não terminou nada agora — é só mais uma
    // consulta dizendo "parado", que é o que ela diz o dia inteiro.
    if (!rodava && !meu) return;

    // A varredura cria identidades, releases e pendências, e mexe nos contadores
    // do menu e nos primeiros passos de T3. Invalidar é do acompanhamento, não do
    // clique: quem estava na tela quando o periódico terminou também precisa ver.
    void queryClient.invalidateQueries({ queryKey: identitiesKeys.todas });
    void queryClient.invalidateQueries({ queryKey: approvalsKeys.todas });
    void queryClient.invalidateQueries({ queryKey: overviewKeys.overview });
    void queryClient.invalidateQueries({ queryKey: tenantKeys.discovery });

    if (!meu) return;
    meuRef.current = false;

    const resultado = lastResultRef.current;
    const aviso = sweepWarning(resultado);
    warningRef.current?.(aviso);
    toast.mostrar(sweepMessage(resultado), { tone: aviso ? 'aviso' : 'sucesso' });
  }, [inExecution, colhidoEm, queryClient, toast]);

  const execute = useCallback(async () => {
    onErro?.(null);
    warningRef.current?.(null);
    // **Antes** do `await`, não depois: com uma varredura curta o desfecho pode
    // chegar antes de a promessa do disparo resolver, e o fecho sairia sem toast.
    meuRef.current = true;
    cliqueEmRef.current = Date.now();
    try {
      await disparo.mutateAsync();
    } catch (erro) {
      if (!isApiError(erro) || erro.code !== CODE_IN_EXECUTION) {
        // Todo caminho de falha do cliente HTTP termina em `ApiError` (client.ts);
        // o `as` cobre o impossível sem esconder o caso do 409 abaixo.
        meuRef.current = false;
        onErro?.(erro as ApiError);
        return;
      }
      // Já havia uma correndo: não é falha, é a mesma varredura vista de outro
      // clique. Entra no acompanhamento, e o fecho vale para este clique também.
      toast.mostrar(concurrencyMessage(erro), { tone: 'info' });
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.status });
    }
  }, [disparo, onErro, queryClient, toast]);

  return {
    emExecucao: inExecution,
    iniciadoEm: status.data?.started_at ?? null,
    iniciadoPor: status.data?.started_by ?? null,
    disparando: disparo.isPending,
    executar: execute,
  };
}

/** "4 imagens varridas · 2 releases novas · 1 na fila" — o toast literal do FR-040. */
export function sweepMessage(resultado: DiscoveryResult | null | undefined): string {
  // As três contagens pelo catálogo (`tn()` escolhe a forma; a interpolação formata o
  // número no idioma). O «·» é separador, não texto — fica aqui.
  return [
    tn('common.discoveryNow.sweep.images', resultado?.images_scanned ?? 0),
    tn('common.discoveryNow.sweep.releases', resultado?.new_releases ?? 0),
    t('common.discoveryNow.sweep.queued', { n: resultado?.queued ?? 0 }),
  ].join(' · ');
}

/**
 * A frase do catálogo para o código do erro de uma varredura que falhou, ou `null` quando o código não tem frase
 * própria (aim-jgy).
 *
 * A tela compõe pelo código, como o resto do console (`error.<code>`), nos dois idiomas. A `error_message` do
 * resultado é a mensagem do domínio, em português, e fica só no log e na trilha: a regra do mrolim é «inglês cru
 * é aceitável, português cru não é».
 *
 * `null` em dois casos, e quem chama diz então a frase genérica com o código ao lado:
 *  - o código não está na tabela (um executor mais novo que ela). `errorSpec` mostraria a mensagem do servidor,
 *    que é a que não se mostra aqui;
 *  - a frase pede um dado que a varredura não tem (`{mensagem}`, `{slug}`…): sem ele, a chave sairia crua.
 *
 * **A frase da varredura vem antes da geral (aim-pkg, 12/10/2026).** O código diz a causa, e é o mesmo na gravação e
 * na varredura. A consequência muda: a gravação diz «nada foi gravado», e a varredura diz «a varredura parou». Por
 * isso a chave própria (`common.discoveryNow.error.<code>`) vence a geral (`error.<code>`), que fica como reserva.
 */
export function sweepErrorPhrase(code: string): string | null {
  const own = `common.discoveryNow.error.${code}`;
  if (hasKey(own)) return t(own);
  if (!hasErrorMessage(code)) return null;
  const phrase = errorSpec(code).mensagem;
  return /\{[^}]*\}/.test(phrase) ? null : phrase;
}

/**
 * O que impediu a varredura de fechar limpa, ou `null`.
 *
 * `error_code` primeiro: uma varredura que parou no meio é mais grave que um
 * aviso, e é a única coisa que explica contagens em zero.
 *
 * Dois ou mais avisos: o primeiro **e quantos ficaram de fora** — a faixa dizia
 * «aviso» no singular sobre dois (achado da visao-geral, 09/09). Resolvido na frase,
 * sem mudar o tipo de `onAviso` (quatro call sites); o plural sai do número cru.
 */
export function sweepWarning(resultado: DiscoveryResult | null | undefined): string | null {
  if (!resultado) return null;
  if (resultado.error_code) {
    const code = resultado.error_code;
    return `${sweepErrorPhrase(code) ?? t('common.discoveryNow.failed')} (${code})`;
  }
  const avisos = resultado.warnings ?? [];
  const [first] = avisos;
  if (!first) return null;
  if (avisos.length === 1) return first;
  return tn('common.discoveryNow.moreWarnings', avisos.length - 1, { first });
}

/**
 * «Discovery já em andamento (desde 14:32).» — o `409` dito como informação.
 *
 * A hora vem do `data.started_at` do próprio erro; sem ela a frase seria "espere"
 * sem dizer por quanto, que é o que fazia o operador clicar de novo.
 */
function concurrencyMessage(erro: ApiError): string {
  const desde = hour(erro.data.started_at);
  return desde
    ? t('common.discoveryNow.inProgressSince', { since: desde })
    : t('common.discoveryNow.inProgress');
}
